import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  buscarAncora,
  listarSugestoes,
  buscarProdutosEquiv,
  vincularMembro,
  descartarMembro,
  removerMembro,
  atualizarAncora,
  removerAncora,
  sugerirPrincipioIA,
} from './api'

function useDebounce<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return debounced
}

export default function EquivalenciaDetail() {
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [buscaManual, setBuscaManual] = useState('')
  const [editando, setEditando] = useState(false)
  const [edNome, setEdNome] = useState('')
  const [edUnidade, setEdUnidade] = useState('')
  const [edCodigo, setEdCodigo] = useState('')
  const [edPrincipio, setEdPrincipio] = useState('')
  const [edConcentracao, setEdConcentracao] = useState('')
  const qManual = useDebounce(buscaManual.trim(), 300)

  const grupoQ = useQuery({
    queryKey: ['ancora', id],
    queryFn: () => buscarAncora(id),
  })
  const sugestoesQ = useQuery({
    queryKey: ['sugestoes', id],
    queryFn: () => listarSugestoes(id),
  })
  const buscaQ = useQuery({
    queryKey: ['busca-equiv', qManual],
    queryFn: () => buscarProdutosEquiv(qManual),
    enabled: qManual.length >= 2,
    placeholderData: keepPreviousData,
  })

  function invalidarTudo() {
    qc.invalidateQueries({ queryKey: ['ancora', id] })
    qc.invalidateQueries({ queryKey: ['sugestoes', id] })
    qc.invalidateQueries({ queryKey: ['busca-equiv'] })
  }

  const mVincular = useMutation({ mutationFn: (cm: string) => vincularMembro(cm, id), onSuccess: invalidarTudo })
  const mDescartar = useMutation({ mutationFn: (cm: string) => descartarMembro(cm), onSuccess: invalidarTudo })
  const mRemover = useMutation({ mutationFn: (cm: string) => removerMembro(cm), onSuccess: invalidarTudo })
  const mSalvarAncora = useMutation({
    mutationFn: () => atualizarAncora(id, {
      nome: edNome.trim(), unidade: edUnidade.trim(), codigo_compra: edCodigo.trim(),
      principio_ativo: edPrincipio.trim() || null, concentracao: edConcentracao.trim() || null,
    }),
    onSuccess: () => { setEditando(false); invalidarTudo() },
  })
  const mIaPrincipio = useMutation({
    mutationFn: () => sugerirPrincipioIA(id),
    onSuccess: (s) => {
      if (s.principio_ativo) setEdPrincipio(s.principio_ativo)
      if (s.concentracao) setEdConcentracao(s.concentracao)
    },
  })
  // 1 clique direto na tela (sem entrar em "editar"): IA extrai E já grava.
  const mDefinirPrincipioIA = useMutation({
    mutationFn: async () => {
      const s = await sugerirPrincipioIA(id)
      if (!s.principio_ativo) throw new Error('A IA não identificou um princípio ativo neste nome — defina manualmente em "editar".')
      await atualizarAncora(id, { principio_ativo: s.principio_ativo, concentracao: s.concentracao ?? null })
      return s
    },
    onSuccess: invalidarTudo,
  })
  const mRemoverAncora = useMutation({
    mutationFn: () => removerAncora(id),
    onSuccess: () => navigate('/equivalencia'),
  })

  if (grupoQ.error)
    return <p className="p-8 text-red-600">Erro: {grupoQ.error instanceof Error ? grupoQ.error.message : String(grupoQ.error)}</p>
  if (grupoQ.isLoading || !grupoQ.data)
    return <p className="p-8 text-slate-500">Carregando…</p>

  const { ancora, membros, estoque_total } = grupoQ.data
  const membroSet = new Set(membros.map((m) => m.codigo))

  function abrirEdicao() {
    setEdNome(ancora.nome)
    setEdUnidade(ancora.unidade ?? '')
    setEdCodigo(ancora.codigo_compra)
    setEdPrincipio(ancora.principio_ativo ?? '')
    setEdConcentracao(ancora.concentracao ?? '')
    setEditando(true)
  }

  return (
    <div className="max-w-[1100px] mx-auto px-4 py-6">
      <Link to="/equivalencia" className="text-sm text-blue-700 hover:underline">← Voltar</Link>

      {/* Cabeçalho da âncora */}
      <div className="mt-3 mb-6 bg-white border border-slate-200 rounded-xl p-5">
        {!editando ? (
          <>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-mono text-xs text-slate-500">
                {ancora.codigo_compra}
                <span className="ml-2 text-slate-400">{ancora.origem_erp ? `(importada do ERP ${ancora.origem_erp})` : '(nativa)'}</span>
              </p>
              <h1 className="text-xl font-bold text-slate-900">{ancora.nome}</h1>
              <p className="text-xs text-slate-500 mt-1">
                unidade {ancora.unidade ?? '—'}
                {ancora.principio_ativo && (
                  <span className="ml-2">· princípio <span className="font-semibold text-slate-700">{ancora.principio_ativo}</span>{ancora.concentracao ? ` ${ancora.concentracao}` : ''}</span>
                )}
              </p>
              <div className="mt-2 flex gap-3">
                <button onClick={abrirEdicao} className="text-xs text-blue-700 hover:underline">editar</button>
                <button
                  onClick={() => { if (confirm('Excluir esta âncora e todos os vínculos?')) mRemoverAncora.mutate() }}
                  className="text-xs text-red-600 hover:underline"
                >
                  excluir âncora
                </button>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Estoque somado</p>
              <p className="text-2xl font-bold tabular-nums text-slate-900">{estoque_total.toLocaleString('pt-BR')}</p>
              <p className="text-xs text-slate-500">{membros.length} membro(s)</p>
            </div>
          </div>

          {!ancora.principio_ativo && (
            <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-purple-200 bg-purple-50 px-4 py-3">
              <span className="text-sm text-purple-900">
                ✦ <span className="font-semibold">Defina o princípio ativo</span> para encontrar marcas equivalentes — mesmo com nomes diferentes.
              </span>
              <button
                type="button"
                onClick={() => mDefinirPrincipioIA.mutate()}
                disabled={mDefinirPrincipioIA.isPending}
                className="ml-auto text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {mDefinirPrincipioIA.isPending ? 'Identificando…' : '✦ Definir com IA'}
              </button>
              {mDefinirPrincipioIA.error && (
                <p className="w-full text-xs text-red-600">
                  {mDefinirPrincipioIA.error instanceof Error ? mDefinirPrincipioIA.error.message : 'Erro'}
                </p>
              )}
            </div>
          )}
          </>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <label className="block text-xs text-slate-600 mb-1">Nome</label>
              <input value={edNome} onChange={(e) => setEdNome(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="w-24">
              <label className="block text-xs text-slate-600 mb-1">Unidade</label>
              <input value={edUnidade} onChange={(e) => setEdUnidade(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="w-32">
              <label className="block text-xs text-slate-600 mb-1">Cód. compra</label>
              <input value={edCodigo} maxLength={10} onChange={(e) => setEdCodigo(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs text-slate-600 mb-1">Princípio ativo (p/ matching)</label>
              <input value={edPrincipio} onChange={(e) => setEdPrincipio(e.target.value)} placeholder="ex: meloxicam"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="w-28">
              <label className="block text-xs text-slate-600 mb-1">Concentração</label>
              <input value={edConcentracao} onChange={(e) => setEdConcentracao(e.target.value)} placeholder="ex: 2mg"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <button type="button" onClick={() => mIaPrincipio.mutate()} disabled={mIaPrincipio.isPending}
              className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-800 font-medium px-3 py-2 rounded-lg disabled:opacity-50">
              {mIaPrincipio.isPending ? 'IA…' : '✦ IA'}
            </button>
            <button onClick={() => mSalvarAncora.mutate()} disabled={!edNome.trim() || mSalvarAncora.isPending}
              className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
              {mSalvarAncora.isPending ? 'Salvando…' : 'Salvar'}
            </button>
            <button onClick={() => setEditando(false)} className="text-sm px-3 py-2 text-slate-500 hover:text-slate-700">Cancelar</button>
            {mSalvarAncora.error && (
              <p className="w-full text-xs text-red-600">{mSalvarAncora.error instanceof Error ? mSalvarAncora.error.message : 'Erro'}</p>
            )}
          </div>
        )}
      </div>

      {/* Membros do grupo */}
      <h2 className="text-sm font-semibold text-slate-700 mb-2">Membros do grupo</h2>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-8">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5">SKU</th>
              <th className="text-left px-4 py-2.5">Produto (marca)</th>
              <th className="text-right px-4 py-2.5">Estoque</th>
              <th className="text-right px-4 py-2.5">Preço</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {membros.map((m) => (
              <tr key={m.codigo} className="border-t border-slate-100">
                <td className="px-4 py-2 font-mono text-xs text-slate-600">{m.codigo}</td>
                <td className="px-4 py-2 text-slate-800">{m.nome}</td>
                <td className="px-4 py-2 text-right tabular-nums">{Number(m.estoque_atual).toLocaleString('pt-BR')}</td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-500">
                  {Number(m.preco).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => mRemover.mutate(m.codigo)} disabled={mRemover.isPending}
                    className="text-xs text-red-600 hover:underline disabled:opacity-40">remover</button>
                </td>
              </tr>
            ))}
            {membros.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Nenhum membro vinculado ainda</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Sugestões automáticas */}
      <h2 className="text-sm font-semibold text-slate-700 mb-1">Sugestões automáticas</h2>
      <p className="text-xs text-slate-500 mb-2">
        {sugestoesQ.data?.principio
          ? <>Por <span className="font-semibold text-emerald-700">princípio ativo "{sugestoesQ.data.principio}"</span> (mesmo de marca diferente) + nome: </>
          : <>Por nome: </>}
        {sugestoesQ.data?.tokens?.map((t) => (
          <span key={t} className="font-mono bg-slate-100 px-1 rounded mx-0.5">{t}</span>
        ))}
      </p>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-8">
        <table className="w-full text-sm">
          <tbody>
            {sugestoesQ.data?.candidatos
              .filter((c) => !membroSet.has(c.codigo))
              .map((c) => (
                <tr key={c.codigo} className="border-t border-slate-100 first:border-t-0">
                  <td className="px-4 py-2 font-mono text-xs text-slate-600 w-20">{c.codigo}</td>
                  <td className="px-4 py-2 text-slate-800">
                    {c.match_principio && (
                      <span className="mr-1.5 text-[10px] font-semibold uppercase text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">princípio</span>
                    )}
                    {c.nome}
                    {c.principio_ativo && <span className="text-xs text-emerald-700 ml-2">{c.principio_ativo}{c.concentracao ? ` ${c.concentracao}` : ''}</span>}
                    <span className="text-xs text-slate-400 ml-2">{c.departamento ?? ''}</span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-500 w-24">
                    {Number(c.estoque_atual).toLocaleString('pt-BR')}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap w-40">
                    <button onClick={() => mVincular.mutate(c.codigo)} disabled={mVincular.isPending}
                      className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">vincular</button>
                    <button onClick={() => mDescartar.mutate(c.codigo)} disabled={mDescartar.isPending}
                      className="text-xs text-slate-400 hover:text-red-600 ml-3" title="Não é equivalente — não sugerir de novo">descartar</button>
                  </td>
                </tr>
              ))}
            {sugestoesQ.data && sugestoesQ.data.candidatos.filter((c) => !membroSet.has(c.codigo)).length === 0 && (
              <tr><td className="px-4 py-6 text-center text-slate-400">Nenhuma sugestão automática</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Adicionar manualmente */}
      <h2 className="text-sm font-semibold text-slate-700 mb-2">Adicionar manualmente</h2>
      <input
        type="text"
        value={buscaManual}
        onChange={(e) => setBuscaManual(e.target.value)}
        placeholder="Buscar qualquer produto por nome ou código…"
        className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {buscaQ.data && buscaQ.data.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {buscaQ.data.map((p) => {
                const jaNesteGrupo = membroSet.has(p.codigo)
                const noutroGrupo = p.ja_vinculado && p.ancora_id !== id
                return (
                  <tr key={p.codigo} className="border-t border-slate-100 first:border-t-0">
                    <td className="px-4 py-2 font-mono text-xs text-slate-600 w-20">{p.codigo}</td>
                    <td className="px-4 py-2 text-slate-800">{p.nome}</td>
                    <td className="px-4 py-2 text-right w-40">
                      {jaNesteGrupo ? (
                        <span className="text-xs text-green-600">já neste grupo</span>
                      ) : noutroGrupo ? (
                        <span className="text-xs text-amber-600" title={`Vinculado a outra âncora (${p.ancora_id})`}>em outro grupo</span>
                      ) : (
                        <button onClick={() => mVincular.mutate(p.codigo)} disabled={mVincular.isPending}
                          className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">vincular</button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
